"use client";

import React, { FC } from "react";
import { FeedbackModal } from "./FeedbackModal";
import { SharingPlatform } from "@/app/@types";

interface Props {
  sharingPlatform: SharingPlatform;
  handleSharingModalContinue: () => void;
}


const PLATFORM_CONFIG = {
  twitter: {
    emoji: "🐦",
    name: "Twitter",
    description: "Twitter should now be open in a new tab with your tweet pre-filled. You can edit and post it!",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-300",
  },
  // instagram: {
  //   emoji: "📷",
  //   name: "Instagram",
  //   description: "Instagram is now open and your text has been copied to your clipboard. Paste it in your post!",
  //   bgColor: "bg-brand-teal/20",
  //   borderColor: "border-brand-teal/30",
  //   textColor: "text-brand-accent",
  // },
  // tiktok: {
  //   emoji: "🎵",
  //   name: "TikTok",
  //   description: "TikTok is now open and your text has been copied to your clipboard. Use it as your video description!",
  //   bgColor: "bg-black/20",
  //   borderColor: "border-gray-500/30",
  //   textColor: "text-gray-300",
  // },
} as const;


const PLATFORM_STEPS = {
  twitter: [
    { emoji: "✏️", text: "Review and edit your pre-filled tweet" },
    { 
      emoji: "🏷️", 
      text: "Add any additional hashtags like",
      code: "#BehavioralEconomics"
    },
    { emoji: "🎯", text: "Tag friends to challenge them" },
    { emoji: "📤", text: "Hit Tweet to share!" },
  ],
  // instagram: [
  //   { emoji: "📋", text: "Your text is copied to clipboard" },
  //   { emoji: "📲", text: "Create a new post or story" },
  //   { emoji: "📝", text: "Paste your text (Ctrl+V / Cmd+V)" },
  //   { 
  //     emoji: "🎯", 
  //     text: "Add hashtag",
  //     code: "#ProbablChallenge",
  //     suffix: " and tag friends!"
  //   },
  // ],
  // tiktok: [
  //   { emoji: "📋", text: "Your text is copied to clipboard" },
  //   { emoji: "🎬", text: "Upload or record your video" },
  //   { emoji: "📝", text: "Paste text in description (Ctrl+V / Cmd+V)" },
  //   { 
  //     emoji: "🏷️", 
  //     text: "Add hashtags",
  //     code: "#BehavioralEconomics #Probabl"
  //   },
  // ],
} as const;


const PRO_TIPS = {
  twitter: "💡 Pro tip: Ask your followers if they can beat your score!",
  // instagram: "💡 Pro tip: Challenge your friends to beat your score!",
  // tiktok: "🔥 Trending tip: Share the most surprising bias you discovered!",
} as const;

interface StepData {
  emoji: string;
  text: string;
  code?: string;
  suffix?: string;
}

interface StepProps {
  step: StepData;
  index: number;
}

const Step: FC<StepProps> = ({ step, index }) => (
  <div>
    {step.emoji} <strong>Step {index + 1}:</strong> {step.text}
    {step.code && (
      <>
        {" "}
        <span className="font-mono bg-black/30 px-1 rounded">
          {step.code}
        </span>
      </>
    )}
    {step.suffix && step.suffix}
  </div>
);

interface StepsGuideProps {
  platform: Exclude<SharingPlatform, null>;
  config: typeof PLATFORM_CONFIG[keyof typeof PLATFORM_CONFIG];
}

const StepsGuide: FC<StepsGuideProps> = ({ platform, config }) => (
  <div className={`${config.bgColor} rounded-lg p-4 mb-6 border ${config.borderColor}`}>
    <div className={`${config.textColor} text-sm space-y-2`}>
      {PLATFORM_STEPS[platform].map((step, index) => (
        <Step key={index} step={step} index={index} />
      ))}
    </div>
  </div>
);

export const SharingPlatformModal: FC<Props> = ({
  sharingPlatform,
  handleSharingModalContinue
}) => {
  // Handle null case
  if (!sharingPlatform) {
    return null;
  }

  const config = PLATFORM_CONFIG[sharingPlatform];

  return (
    <FeedbackModal
      type="info"
      handleContinue={handleSharingModalContinue}
      showConfetti={false}
    >
      <div className="text-6xl mb-4">
        {config.emoji}
      </div>

      <h2 className="text-2xl font-bold text-white mb-4">
        Share to {config.name}
      </h2>

      <p className="text-gray-200 mb-6 leading-relaxed">
        {config.description}
      </p>

      <StepsGuide platform={sharingPlatform} config={config} />

      <div className="text-sm text-gray-400">
        {PRO_TIPS[sharingPlatform]}
      </div>
    </FeedbackModal>
  );
};